/**
 * GHS Warnings Module
 * Optimized for non-blocking performance with aggressive timeouts and caching
 */

class GHSWarnings {
    constructor() {
        this.cache = new Map();
        this.sessionStorageKey = 'ghs_warnings_cache';
        this.cacheTimeout = 24 * 60 * 60 * 1000;
        // WordPress itself gives the upstream request 3 seconds. Leave enough
        // room for admin-ajax bootstrap/JSON encoding before aborting locally.
        this.apiTimeout = 4500;

        // Load cached data from sessionStorage on initialization
        this.loadCacheFromStorage();

        // Initialize after DOM is ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.init());
        } else {
            // Use setTimeout to ensure all other scripts have loaded
            setTimeout(() => this.init(), 100);
        }
    }

    init() {
        const container = document.getElementById('ghs-warnings-container');
        if (!container) {
            return; // No GHS container on this page
        }

        const sku = container.dataset.sku;
        if (!sku) {
            console.warn('GHS: No SKU found for warnings');
            return;
        }

        const hasSnapshot = container.dataset.hasSnapshot === '1';
        const cachedData = this.getCachedData(sku);
        if (!hasSnapshot && cachedData) {
            this.renderGHSWarnings(cachedData, sku);
            return;
        }

        // The public GHS endpoint is protected by Cloudflare in production.
        // Never let every PDP visitor retry a known-unreachable upstream. The
        // server-rendered product snapshot/strength fallback remains visible;
        // client refresh is enabled only when WordPress has an explicit origin.
        if (container.dataset.clientRefresh !== '1') {
            return;
        }

        const needsRefresh = container.dataset.needsRefresh === '1';
        if (hasSnapshot && !needsRefresh) {
            return;
        }

        // The product-local snapshot or strength fallback stays visible while
        // a stale entry is refreshed. No visitor waits on an empty skeleton.
        setTimeout(() => {
            this.fetchGHSData(sku);
        }, hasSnapshot ? 1000 : 0);
    }

    loadCacheFromStorage() {
        try {
            const cached = sessionStorage.getItem(this.sessionStorageKey);
            if (cached) {
                const data = JSON.parse(cached);
                // Validate cache timestamps
                Object.entries(data).forEach(([sku, item]) => {
                    if (Date.now() - item.timestamp < this.cacheTimeout) {
                        this.cache.set(sku, item.data);
                    }
                });
            }
        } catch (e) {
            console.warn('GHS: Error loading cache from storage:', e);
        }
    }

    saveCacheToStorage() {
        try {
            const cacheObject = {};
            this.cache.forEach((data, sku) => {
                cacheObject[sku] = {
                    data: data,
                    timestamp: Date.now()
                };
            });
            sessionStorage.setItem(this.sessionStorageKey, JSON.stringify(cacheObject));
        } catch (e) {
            console.warn('GHS: Error saving cache to storage:', e);
        }
    }

    getCachedData(sku) {
        return this.cache.get(sku);
    }

    setCachedData(sku, data) {
        this.cache.set(sku, data);
        this.saveCacheToStorage();
    }

    async fetchGHSData(sku) {
        const container = document.getElementById('ghs-warnings-container');
        if (!container) return;

        const fallback = document.getElementById('ghs-fallback');
        const hasVisibleFallback = fallback
            && fallback.style.display !== 'none'
            && fallback.textContent.trim().length > 0;
        if (!hasVisibleFallback && container.dataset.hasSnapshot !== '1') {
            this.showLoadingState();
        }

        let timeoutId;
        try {
            const controller = new AbortController();
            timeoutId = setTimeout(() => controller.abort(), this.apiTimeout);

            const formData = new FormData();
            formData.append('action', 'get_ghs_warnings');
            formData.append('sku', sku);
            formData.append('nonce', ajax_var.nonce);

            const response = await fetch(ajax_var.url, {
                method: 'POST',
                body: formData,
                signal: controller.signal,
                credentials: 'same-origin'
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();

            if (result.success) {
                if (result.data.cache_status !== 'stale') {
                    this.setCachedData(sku, result.data);
                }
                this.renderGHSWarnings(result.data, sku);
                container.dataset.hasSnapshot = '1';
                container.dataset.needsRefresh = result.data.cache_status === 'stale' ? '1' : '0';
            } else {
                throw new Error(result.data || 'Unknown API error');
            }

        } catch (error) {
            console.warn('GHS: API refresh failed:', error.message);
            if (container.dataset.hasSnapshot === '1') {
                this.hideLoadingState();
            } else {
                this.showFallbackState();
            }
        } finally {
            if (timeoutId) clearTimeout(timeoutId);
        }
    }

    showLoadingState() {
        const loading = document.getElementById('ghs-loading');
        const wrapper = document.getElementById('ghs-warnings-container');
        if (wrapper) {
            wrapper.style.minHeight = '160px';
        }
        if (loading) {
            loading.style.display = 'block';
            setTimeout(() => {
                if (loading.style.display === 'block') {
                    loading.style.opacity = '1';
                }
            }, 50);
        }
    }

    hideLoadingState() {
        const loading = document.getElementById('ghs-loading');
        if (loading) {
            loading.style.display = 'none';
            loading.style.opacity = '0';
        }
    }

    showFallbackState() {
        this.hideLoadingState();
        const fallback = document.getElementById('ghs-fallback');
        const wrapper = document.getElementById('ghs-warnings-container');
        if (wrapper) {
            wrapper.style.minHeight = 'auto';
        }
        if (fallback) {
            fallback.style.display = 'block';
            fallback.style.opacity = '1';
        }
    }

    renderGHSWarnings(data, sku) {
        this.hideLoadingState();

        const dynamicContainer = document.getElementById('ghs-dynamic');
        const fallbackContainer = document.getElementById('ghs-fallback');
        const wrapper = document.getElementById('ghs-warnings-container');

        if (!dynamicContainer) return;

        // Generate HTML for GHS warnings
        const ghsHtml = this.generateGHSHTML(data);

        if (ghsHtml) {
            dynamicContainer.innerHTML = ghsHtml;

            // Smooth transition to show GHS content and hide fallback
            dynamicContainer.style.display = 'block';
            setTimeout(() => {
                if (wrapper) {
                    wrapper.style.minHeight = 'auto';
                }
                dynamicContainer.style.opacity = '1';
                if (fallbackContainer) {
                    fallbackContainer.style.opacity = '0';
                    setTimeout(() => {
                        fallbackContainer.style.display = 'none';
                    }, 300);
                }
            }, 50);
        } else {
            // No GHS data available, keep fallback
            this.showFallbackState();
        }
    }

    generateGHSHTML(data) {
        if (!data) {
            return '';
        }

        let html = '';

        // Group pictograms and statements logically
        const warnings = this.groupWarnings(data);

        warnings.forEach((warning, index) => {
            const hazardLines = Array.isArray(warning.hazard_statements)
                ? [...warning.hazard_statements]
                : [];
            const hazardText = hazardLines
                .map(line => (line || '').replace(/\s+/g, ' ').trim())
                .filter(line => line.length > 0)
                .join(' ');

            const precautionaryLines = Array.isArray(warning.precautionary_statements)
                ? warning.precautionary_statements
                : [];
            const precautionaryText = precautionaryLines
                .map(line => (line || '').replace(/\s+/g, ' ').trim())
                .filter(line => line.length > 0)
                .join(' ');

            const imageUrl = this.escapeHtml(warning.svg_url || '');
            const imageAlt = this.escapeHtml(warning.alt_label || warning.label || 'GHS');
            const signalWord = this.escapeHtml(warning.signal_word || '');
            const statementText = this.escapeHtml(`${hazardText} ${precautionaryText}`.trim());

            html += `
                <div class="ghs-warning-item">
                    ${imageUrl ? `<div class="ghs-pictogram">
                        <img src="${imageUrl}"
                             alt="${imageAlt}"
                             width="50"
                             height="50"
                             onerror="this.style.display='none'">
                    </div>` : ''}
                    <div class="ghs-content">
                        ${signalWord ? `<div class="ghs-hazard-title">${signalWord}</div>` : ''}
                        ${statementText ?
                            `<div class="ghs-hazard-text">${statementText}</div>` : ''}
                    </div>
                </div>
            `;
        });

        return html;
    }

    groupWarnings(data) {
        if (Array.isArray(data.pictograms) && data.pictograms.length > 0) {
            return data.pictograms.map(pictogram => ({
                svg_url: pictogram.svg_url,
                label: pictogram.label,
                alt_label: pictogram.alt_label,
                signal_word: pictogram.signal_word || data.signal_word,
                hazard_statements: pictogram.hazard_statements || [],
                precautionary_statements: pictogram.precautionary_statements || []
            }));
        }

        const hazardStatements = Array.isArray(data.hazard_statements)
            ? data.hazard_statements
            : [];
        const precautionaryStatements = Array.isArray(data.precautionary_statements)
            ? data.precautionary_statements
            : [];
        if (hazardStatements.length === 0 && precautionaryStatements.length === 0) {
            return [];
        }

        return [{
            svg_url: '',
            label: 'GHS',
            alt_label: 'GHS',
            signal_word: data.signal_word || '',
            hazard_statements: hazardStatements,
            precautionary_statements: precautionaryStatements
        }];
    }

    escapeHtml(value) {
        const div = document.createElement('div');
        div.textContent = String(value);
        return div.innerHTML;
    }
}

// Initialize GHS warnings system
window.GHSWarnings = GHSWarnings;

// Auto-initialize when the script loads
const ghsWarnings = new GHSWarnings();

export default GHSWarnings;
